import { Component, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CanDisable, mixinDisabled } from '@angular/material/core';
import {
  BazisMaterialHelper,
  CuttingFacadeMaterial,
  CuttingMaterial,
  CuttingPanelListService,
  CuttingSettings,
  FacadeView,
  LegacyDialogsService,
  NgxLocalizedNumbersService,
} from 'cloud-shared-lib';
import * as _ from 'lodash';
import { Subject } from 'rxjs';

import { PanelListTableComponent } from './panel-list-table/panel-list-table.component';

export interface GroupedFacadesView extends CuttingFacadeMaterial {
  facades: FacadeView[];
}

export class PanelListBase {}

// eslint-disable-next-line no-underscore-dangle
export const _PanelListMixinBase = mixinDisabled(PanelListBase);

@Component({
  selector: 'cutting-panel-list',
  templateUrl: './panel-list.component.html',
  styleUrls: ['./panel-list.component.scss'],
  // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
  inputs: ['disabled'],
  standalone: true,
  imports: [PanelListTableComponent],
})
export class PanelListComponent extends _PanelListMixinBase implements OnChanges, CanDisable {
  @ViewChild(PanelListTableComponent) table: PanelListTableComponent;

  @Input() panels: FacadeView[] = [];
  @Input() editMode = false;
  @Input({ required: true }) cuttingSettings: CuttingSettings;

  @Output() editing: Subject<FacadeView> = new Subject();

  materials: GroupedFacadesView[] = [];
  editablePanelUuid = '';

  constructor(
    private dialogs: LegacyDialogsService,
    private localizedNumbersService: NgxLocalizedNumbersService,
    private cuttingPanelListService: CuttingPanelListService
  ) {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.panels?.currentValue) {
      this.materials = this.groupPanels(changes.panels.currentValue);
    }
  }

  delete(panel: FacadeView): void {
    const index = this.panels.indexOf(panel);
    if (index >= 0) {
      this.panels.splice(index, 1);
      this.materials = this.groupPanels(this.panels);
    }
  }

  edit(item: FacadeView): void {
    this.editablePanelUuid = item.uuid;
    this.editing.next(item);
  }

  copy(item: FacadeView): void {
    this.editablePanelUuid = item.uuid;
    this.panels = [item, ...this.panels];
    this.materials = this.groupPanels(this.panels);
  }

  changeEdges(newEdges: FacadeView[]): void {
    newEdges.forEach(e => {
      const existed = this.panels.find(p => p.uuid === e.uuid);

      if (existed) {
        existed.l1 = e.l1;
        existed.l2 = e.l2;
        existed.w1 = e.w1;
        existed.w2 = e.w2;
      }
    });

    this.materials = this.groupPanels(this.panels);
  }

  changeMaterial(event: { facades: FacadeView[]; newMaterial: CuttingMaterial }): void {
    const newFacadeMaterial = _.cloneDeep(event.facades[0].material);
    this.replaceMaterialsFromMB(newFacadeMaterial, event.newMaterial);

    event.facades.forEach(e => {
      const existed = this.panels.find(p => p.uuid === e.uuid);

      if (existed) {
        existed.material = newFacadeMaterial;
      }
    });

    this.materials = this.groupPanels(this.panels);
  }

  private replaceMaterialsFromMB(source: CuttingFacadeMaterial, target: CuttingMaterial): void {
    source.id = 0;
    source.inMaterialBaseGroupId = target.inMaterialBaseGroupId;
    source.inMaterialBaseId = target.inMaterialBaseId;
    source.name = target.name;
    source.article = target.article;
    source.inMaterialBaseId = target.inMaterialBaseId;
    source.inMaterialBaseGroupId = target.inMaterialBaseGroupId;
    source.textureName = target.textureName;
    source.color = target.color;
    source.thickness = target.thickness;
    source.height = target.height;
    source.width = target.width;
  }

  private groupPanels(source: FacadeView[]): GroupedFacadesView[] {
    const materials: GroupedFacadesView[] = [];

    source.forEach(p => {
      const inGroup = materials.find(BazisMaterialHelper.materialComparer(p.material));
      if (inGroup) {
        inGroup.facades.push(p);
      } else {
        materials.push({
          article: p.material.article,
          facades: [p],
          id: p.material.id,
          name: p.material.name,
          groupId: p.material.groupId,
          measureName: p.material.measureName,
          price: p.material.price,
          weightPerItem: p.material.weightPerItem,
          color: p.material.color,
          height: p.material.height,
          width: p.material.width,
          inMaterialBaseGroupId: p.material.inMaterialBaseGroupId,
          inMaterialBaseId: p.material.inMaterialBaseId,
          textureName: p.material.textureName,
          thickness: p.material.thickness,
          type: p.material.type,
        } as GroupedFacadesView);
      }
    });

    return materials.sort((a, b) => (a.name + a.article).localeCompare(b.name + b.article));
  }
}
